import { useApolloClient } from '@apollo/client';
import {
  DEFAULT_MAX_SUGGESTIONS,
  validateAppNamesV2,
} from '@openx/components-targeting/Targeting/AppInventory/utils/validateAppNamesV2';
import type { ValidationResultV2 } from '@openx/components-targeting/Targeting/shared/BulkAdd/types';
import type { AppGroupSearchEntry } from '@openx/types/targeting/inventoryAndContent';
import { useCallback } from 'react';

import {
  AppGroupSearchDocument,
  type AppGroupSearchQuery,
  type AppGroupSearchQueryVariables,
} from '../../../../graphql/query/options/AppGroupSearch.generated';

/**
 * Hook for App Name Bulk Add validation with suggestions.
 * Backed by the GraphQL `appGroupSearch` query (pg_trgm fuzzy match). All matching/dedup/error
 * logic is shared with unity/REST apps via `validateAppNamesV2`; this hook only wires up the
 * Apollo transport and normalizes the response into the canonical `AppGroupSearchEntry` shape.
 */
export const useAppNameBulkValidationV2 = () => {
  const client = useApolloClient();

  const validateAppNames = useCallback(
    (items: string[], existingItems: string[] = []): Promise<ValidationResultV2> =>
      validateAppNamesV2({
        items,
        existingItems,
        maxSuggestions: DEFAULT_MAX_SUGGESTIONS,
        searchByNames: async names => {
          const { data } = await client.query<AppGroupSearchQuery, AppGroupSearchQueryVariables>({
            query: AppGroupSearchDocument,
            variables: { names, suggestion_limit: DEFAULT_MAX_SUGGESTIONS },
            fetchPolicy: 'network-only',
          });

          const resultByInput = new Map<string, AppGroupSearchEntry>();
          for (const entry of data?.appGroupSearch ?? []) {
            resultByInput.set(entry.input.toLowerCase(), {
              match: entry.match ?? null,
              suggestions: entry.suggestions,
            });
          }

          return resultByInput;
        },
      }),
    [client]
  );

  return {
    validateAppNames,
    isReady: true,
  };
};
